import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { firestore } from "../firebase";
import { collection, query, orderBy, getDocs } from "firebase/firestore";
import SEO from "./SEO";

/* ---------- Layout ---------- */

const Page = styled.main`
  min-height: 70vh;
  padding: 40px 18px;
  background: radial-gradient(1200px 400px at 20% 0%, rgba(15, 118, 110, 0.10), transparent 55%),
    radial-gradient(900px 360px at 90% 10%, rgba(195, 122, 71, 0.14), transparent 55%),
    #f7fafc;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Hero = styled.header`
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 18px 55px rgba(0, 0, 0, 0.08);
  border-radius: 22px;
  padding: 22px;
  margin-bottom: 18px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(24px, 3vw, 44px);
  color: #111827;
  font-weight: 950;
  line-height: 1.12;
`;

const Subtitle = styled.p`
  margin: 10px 0 0;
  color: #6b7280;
  font-weight: 800;
  line-height: 1.6;
`;

/* ---------- Grid ---------- */

const Card = styled.section`
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 14px 44px rgba(0, 0, 0, 0.07);
  border-radius: 22px;
  overflow: hidden;
`;

const CardBody = styled.div`
  padding: 18px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 14px;
`;

/* ---------- Competition Item ---------- */

const Item = styled.article`
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.06);
  transition: transform 160ms ease, box-shadow 160ms ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 44px rgba(0, 0, 0, 0.10);
  }
`;

const ImgWrap = styled.div`
  position: relative;
  background: #000;
`;

const Img = styled.img`
  width: 100%;
  height: 190px;
  object-fit: cover;
  display: block;
`;

const Badge = styled.div`
  position: absolute;
  left: 12px;
  top: 12px;
  padding: 8px 12px;
  border-radius: 999px;
  font-weight: 950;
  font-size: 12px;
  color: #fff;
  background: rgba(11, 18, 32, 0.62);
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(6px);
`;

const Body = styled.div`
  padding: 12px 14px 14px;
`;

const Name = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 950;
  color: #111827;
  line-height: 1.35;
`;

const Type = styled.div`
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  padding: 8px 12px;
  border-radius: 999px;

  font-weight: 950;
  font-size: 12px;
  color: #0f766e;
  background: rgba(15, 118, 110, 0.10);
  border: 1px solid rgba(15, 118, 110, 0.20);
`;

const Links = styled.div`
  margin-top: 12px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const ResultBtn = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  padding: 10px 14px;
  border-radius: 999px;

  border: 1px solid #e5e7eb;
  background: #fff;
  color: #111827;
  font-weight: 950;
  text-decoration: none;
  transition: transform 140ms ease, background 140ms ease;

  &:hover {
    background: #f9fafb;
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid rgba(15, 118, 110, 0.45);
    outline-offset: 3px;
  }
`;

/* ---------- States ---------- */

const Empty = styled.div`
  padding: 18px;
  color: #6b7280;
  font-weight: 900;
`;

const SkeletonRow = styled.div`
  height: 84px;
  border-radius: 16px;
  background: linear-gradient(90deg, #f3f4f6, #fafafa, #f3f4f6);
  background-size: 200% 100%;
  animation: shimmer 1.1s infinite linear;

  @keyframes shimmer {
    0% { background-position: 0% 0; }
    100% { background-position: 200% 0; }
  }
`;

/* ---------- Component ---------- */

const DEFAULT_IMAGE_URL =
  "https://firebasestorage.googleapis.com/v0/b/hoforsbgk.appspot.com/o/competitions%2Fblank.png?alt=media&token=651c0dd1-de67-48b2-85d9-c390448aac97";

const Competitions = () => {
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompetitions = async () => {
      try {
        setLoading(true);
        const ref = collection(firestore, "competitions");
        const q = query(ref, orderBy("createdAt", "desc"));
        const snap = await getDocs(q);
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setCompetitions(data);
      } finally {
        setLoading(false);
      }
    };

    fetchCompetitions();
  }, []);

  const items = useMemo(() => competitions || [], [competitions]);

  return (
    <>
      <SEO
        title="Tävlingar"
        description="Tävlingar och resultat för Hofors BGK."
        keywords="tävlingar, resultat, hofors bgk"
      />

      <Page>
        <Container>
          <Hero>
            <Title>Tävlingar</Title>
            <Subtitle>Här hittar du tävlingar och länkar till resultat.</Subtitle>
          </Hero>

          <Card>
            <CardBody>
              {loading ? (
                <>
                  <SkeletonRow />
                  <div style={{ height: 12 }} />
                  <SkeletonRow />
                  <div style={{ height: 12 }} />
                  <SkeletonRow />
                </>
              ) : items.length === 0 ? (
                <Empty>Beklagar, inga tävlingar är inlagda.</Empty>
              ) : (
                <Grid>
                  {items.map((c) => {
                    const img = c.image || DEFAULT_IMAGE_URL;
                    const type = c.type || "Tävling";
                    const name = c.name || "Namnlös tävling";
                    const links = Array.isArray(c.links) ? c.links.filter(Boolean) : [];

                    return (
                      <Item key={c.id}>
                        <ImgWrap>
                          <Img src={img} alt={name} loading="lazy" />
                          <Badge>Hofors BGK</Badge>
                        </ImgWrap>

                        <Body>
                          <Name>{name}</Name>
                          <Type>{type}</Type>

                          {links.length > 0 && (
                            <Links>
                              {links.map((link, i) => (
                                <ResultBtn
                                  key={`${c.id}-link-${i}`}
                                  href={link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Resultat {i + 1}
                                </ResultBtn>
                              ))}
                            </Links>
                          )}
                        </Body>
                      </Item>
                    );
                  })}
                </Grid>
              )}
            </CardBody>
          </Card>
        </Container>
      </Page>
    </>
  );
};

export default Competitions;












